import React, { useEffect, useState } from 'react';
import { GoogleLogin } from '../components/GoogleLogin';
import { AppleLogin } from '../components/AppleLogin';
import { useAuth } from '../contexts/AuthContext';
import type { AppleLoginPayload } from '../lib/authTypes';
import { analytics } from '../lib/analytics';
import {
  buildProviderLoginFailedProps,
  LOGIN_ANALYTICS_SOURCE,
} from '../lib/analytics/loginObservability';
import { ViewMode, ToastMessage } from '../types';
import { LoadingAnimation } from '../components/ui/LoadingAnimation';

interface LoginPageProps {
  viewMode: ViewMode;
  onShowToast: (toast: Omit<ToastMessage, 'id'>) => void;
}

export default function LoginPage({ viewMode, onShowToast }: LoginPageProps) {
  const { loginWithGoogle, loginWithApple } = useAuth();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    analytics.trackLoginScreenViewed({
      source: LOGIN_ANALYTICS_SOURCE,
      view_mode: viewMode,
    });
  }, [viewMode]);

  const handleSuccessfulLogin = async (loginAction: () => Promise<void>) => {
    setIsLoggingIn(true);
    try {
      await loginAction();
      onShowToast({
        type: 'success',
        title: 'Welcome to Insight Atlas!',
        description: 'You have been successfully logged in.'
      });
    } catch (error) {
      console.error('Login failed:', error);
      onShowToast({
        type: 'error',
        title: 'Login Failed',
        description: error instanceof Error ? error.message : 'Failed to log in. Please try again.'
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleGoogleSuccess = async (credential: string) => {
    analytics.trackAuthCtaClicked({ method: 'google', source: LOGIN_ANALYTICS_SOURCE });
    await handleSuccessfulLogin(() => loginWithGoogle(credential));
  };

  const handleAppleSuccess = async (payload: AppleLoginPayload) => {
    analytics.trackAuthCtaClicked({ method: 'apple', source: LOGIN_ANALYTICS_SOURCE });
    await handleSuccessfulLogin(() => loginWithApple(payload));
  };

  const handleAuthError = (method: 'google' | 'apple', error: string) => {
    console.error('Authentication error:', error);
    analytics.trackLoginFailed({
      ...buildProviderLoginFailedProps(method, error),
      view_mode: viewMode,
    });
    onShowToast({
      type: 'error',
      title: 'Authentication Error',
      description: error
    });
  };

  const isMobile = viewMode === 'mobile';

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center relative overflow-hidden">
      {/* Animated background orbs - only on desktop */}
      {!isMobile && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Primary floating orb - slow drift */}
          <div 
            className="absolute w-[500px] h-[500px] rounded-full blur-[100px] opacity-[0.04]"
            style={{
              background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)',
              top: '10%',
              left: '5%',
              animation: 'floatOrb1 20s ease-in-out infinite',
            }}
          />
          {/* Secondary orb - opposite drift */}
          <div 
            className="absolute w-[400px] h-[400px] rounded-full blur-[80px] opacity-[0.03]"
            style={{
              background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 70%)',
              bottom: '5%',
              right: '10%',
              animation: 'floatOrb2 25s ease-in-out infinite',
            }}
          />
          {/* Tertiary accent orb - subtle pulse */}
          <div 
            className="absolute w-[300px] h-[300px] rounded-full blur-[60px] opacity-[0.025]"
            style={{
              background: 'radial-gradient(circle, rgba(255,255,255,0.5) 0%, transparent 60%)',
              top: '50%',
              right: '20%',
              animation: 'floatOrb3 18s ease-in-out infinite',
            }}
          />
        </div>
      )}
      
      {/* Mobile: static subtle background */}
      {isMobile && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white/[0.015] rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-white/[0.01] rounded-full blur-3xl" />
        </div>
      )}
      
      {/* Subtle grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />
      
      {/* CSS Animations */}
      <style>{`
        @keyframes floatOrb1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(80px, 40px) scale(1.05); }
          50% { transform: translate(40px, 80px) scale(0.95); }
          75% { transform: translate(-40px, 40px) scale(1.02); }
        }
        @keyframes floatOrb2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-60px, -30px) scale(1.08); }
          66% { transform: translate(30px, -60px) scale(0.92); }
        }
        @keyframes floatOrb3 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.025; }
          50% { transform: translate(-30px, 50px) scale(1.1); opacity: 0.04; }
        }
      `}</style>

      <div className={`w-full max-w-md mx-auto ${isMobile ? 'px-4' : 'px-8'} relative z-10`}>
        {/* Glass card */}
        <div className="bg-white/[0.03] backdrop-blur-xl rounded-3xl border border-white/10 p-8 shadow-2xl">
          
          {/* Logo and Title */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <img 
                src="/logo192.png" 
                alt="Insight Atlas" 
                className="w-full h-full rounded-2xl shadow-lg"
              />
              {/* Subtle glow behind logo */}
              <div className="absolute inset-0 bg-white/10 rounded-2xl blur-xl -z-10" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-3 tracking-tight">
              Insight Atlas
            </h1>
            <p className="text-white/50 text-lg">
              Weeks of research. In minutes.
            </p>
          </div>

          {/* Login Form */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-semibold text-white mb-2">
                Welcome back
              </h2>
              <p className="text-white/40 text-sm leading-relaxed">
                Sign in to access your research and explore discoveries from the community.
              </p>
            </div>

            {/* Google Login Button */}
            <div className="space-y-4">
              {isLoggingIn ? (
                <div className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center justify-center">
                  <div className="flex items-center gap-3">
                    <LoadingAnimation size={20} />
                    <span className="text-white/60">Signing you in...</span>
                  </div>
                </div>
              ) : (
                <div className="sso-provider-stack" data-testid="sso-provider-stack">
                  <div className="sso-provider-shell">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={(error) => handleAuthError('google', error)}
                    />
                  </div>
                  <AppleLogin
                    onSuccess={handleAppleSuccess}
                    onError={(error) => handleAuthError('apple', error)}
                  />
                </div>
              )}
            </div>

            {/* Features */}
            <div className="border-t border-white/10 pt-8">
              <h3 className="text-sm font-medium text-white/70 mb-4 uppercase tracking-wider">
                What you can do
              </h3>
              <ul className="space-y-3">
                {[
                  'Conduct deep AI-powered research',
                  'Explore research from the community',
                  'Get real-time notifications',
                  'Save and organize articles'
                ].map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-white/50">
                    <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-white/5">
            <p className="text-xs text-center text-white/30">
              By signing in, you agree to our terms of service and privacy policy.
            </p>
          </div>
        </div>

        {/* Bottom branding */}
        <div className="text-center mt-8">
          <p className="text-white/20 text-xs">
            © 2025 Insight Atlas. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
